import { ReactElement, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import Signin from './component/Signin';
import { Alert } from '@/components/Alert';

function Login(): ReactElement {
    const [searchParams] = useSearchParams();
    const error = searchParams.get('error');
    const sessionExpired = searchParams.get('session_expired');
    const [errorMessage, setErrorMessage] = useState<ReactNode[]>([]);
    const errorRef = useRef<HTMLDivElement | null>(null);

    const { t, i18n } = useTranslation('login');
    const metaTitle = t('meta.title');
    const metaDescription = t('meta.description');
    const errorTexts = useMemo(() => t('error', { returnObjects: true }) as any, [t]);
    const currentLang = (i18n.language || 'en').split('-')[0];
    const htmlLang = currentLang === 'ja' ? 'ja' : 'en';
    const brandName = htmlLang === 'en' ? 'Kotomil' : 'コトミル';
    const baseUrl = 'https://kotomil.com/login';
    const enUrl = `${baseUrl}?lang=en`;
    const canonicalUrl = currentLang === 'en' ? enUrl : baseUrl;
    const ogLocale = htmlLang === 'ja' ? 'ja_JP' : 'en_US';
    const ogAlternateLocale = htmlLang === 'ja' ? 'en_US' : 'ja_JP';
    const alternateLinks = [
        { lang: 'ja', href: baseUrl },
        { lang: 'en', href: enUrl },
        { lang: 'x-default', href: enUrl },
    ];

    useEffect(() => {
        let message: ReactNode[] = [];
        let shouldDisplay = false;

        if (sessionExpired) {
            message = [errorTexts.sessionExpired];
            shouldDisplay = true;
        } else if (error) {
            shouldDisplay = true;
            switch (error) {
                case 'auth_error':
                    message = [errorTexts.authError];
                    break;
                case 'server_error':
                    message = [errorTexts.serverError];
                    break;
                case 'provider_used_by_otheruser':
                    message = [
                        <div key="provider-error" className="space-y-3">
                            <div>{errorTexts.providerUsed.line1}</div>
                            <div>{errorTexts.providerUsed.line2}</div>
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: errorTexts.providerUsed.line3,
                                }}
                            />
                        </div>,
                    ];
                    break;
                default:
                    message = [errorTexts.default];
            }
        }

        if (shouldDisplay) {
            setErrorMessage(message);
            window.history.replaceState({}, '', window.location.pathname);

            setTimeout(() => {
                if (errorRef.current) {
                    errorRef.current.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                    });
                }
            }, 200);
        }
    }, [error, sessionExpired, errorTexts]);

    return (
        <>
            <Helmet htmlAttributes={{ lang: htmlLang }}>
                <title>{metaTitle}</title>
                <meta name="description" content={metaDescription} />
                <link rel="canonical" href={canonicalUrl} />
                {alternateLinks.map(({ lang, href }) => (
                    <link key={lang} rel="alternate" hrefLang={lang} href={href} />
                ))}
                <meta property="og:type" content="website" />
                <meta property="og:title" content={metaTitle} />
                <meta property="og:description" content={metaDescription} />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:site_name" content={brandName} />
                <meta
                    property="og:image"
                    content="https://kotomil.com/images/ogp/kotomil_og_image.jpg"
                />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
                <meta property="og:locale" content={ogLocale} />
                <meta property="og:locale:alternate" content={ogAlternateLocale} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:site" content="@KotomilApp" />
                <meta name="twitter:title" content={metaTitle} />
                <meta name="twitter:description" content={metaDescription} />
                <meta
                    name="twitter:image"
                    content="https://kotomil.com/images/ogp/kotomil_og_image.jpg"
                />
            </Helmet>
            <div className="flex flex-col items-center justify-center bg-[url('@/assets/images/login/after-login-image.png')] bg-cover bg-center h-screen w-full">
                {errorMessage.length > 0 && (
                    <div ref={errorRef} className="w-full lg:w-1/2 text-center">
                        <Alert
                            type="danger"
                            messages={errorMessage}
                            className="mx-2 my-16 -mb-16 p-6"
                        />
                    </div>
                )}
                <div className="w-full flex flex-col items-center justify-center">
                    <Signin />
                </div>
            </div>
        </>
    );
}

export default Login;
